<template>
    <div class="household-expense-flow">
        <!-- Expense type selection -->
        <section v-if="msg.type === 'household_expense_flow' || msg.type === 'household_expense_type_prompt'" class="hh-card hh-flow-card">
            <header class="hh-flow-card__header"><span>{{ responseText }}</span></header>
            <div class="hh-flow-card__options">
                <button type="button" class="hh-option" :disabled="disabled" @click="chooseType('one_time')"><span>One Time</span></button>
                <button type="button" class="hh-option" :disabled="disabled" @click="chooseType('recurring')"><span>Recurring</span></button>
            </div>
        </section>

        <!-- Generic household option selection (category, frequency, collector, vendor, etc.) -->
        <section v-else-if="msg.type === 'household_expense_option_prompt'" class="hh-card hh-flow-card">

            <!-- Vendor selection -->
            <template v-if="isVendorPrompt">
                <div class="hh-vendor-options">
                    <div class="hh-vendor-title hh-vendor-title--blue">Select Vendor</div>

                    <button v-for="option in vendorPromptOptions"
                            :key="String(optionValue(option))"
                            type="button"
                            class="hh-vendor-selectable"
                            :class="{ 'hh-vendor-selected': isVendorSelected(option) }"
                            :disabled="disabled"
                            @click="selectVendor(option)">
                        <span class="hh-vendor-radio">
                            <span v-if="isVendorSelected(option)" class="hh-vendor-radio__dot"></span>
                        </span>
                        <span class="hh-vendor-name">{{ optionLabel(option) }}</span>
                    </button>
                    <button type="button"
                            class="hh-vendor-skip"
                            :disabled="disabled"
                            @click="skipVendor">
                        Skip
                    </button>
                </div>
            </template>

            <!-- Existing normal option selection -->
            <template v-else>
                <header class="hh-flow-card__header">
                    <span>{{ responseText }}</span>
                </header>

                <div class="hh-flow-card__options">
                    <button v-for="option in optionList"
                            :key="String(optionValue(option))"
                            type="button"
                            class="hh-option"
                            :disabled="disabled"
                            @click="chooseOption(option)">
                        <span>{{ optionLabel(option) }}</span>
                    </button>
                </div>
            </template>
        </section>

        <!-- Household one-time / recurring expense form -->
        <section v-else-if="msg.type === 'household_expense_form'" class="hh-card hh-form-card">
            <header class="hh-form-card__header">
                <div>
                    <h4>{{ isRecurring ? 'Recurring' : 'One Time' }} Expense</h4>
                    <p>{{ responseText || 'Please review before submitting' }}</p>
                </div>
            </header>

            <div class="hh-form-card__body">
                <label class="hh-row">
                    <span>Title <em>*</em></span>
                    <input v-model.trim="form.expenseTitle" :disabled="disabled" type="text" />
                </label>

                <label class="hh-row">
                    <span>Category</span>
                    <Dropdown v-model="form.categoryId"
                              :options="categories"
                              optionLabel="text"
                              optionValue="value"
                              placeholder="Select..."
                              :disabled="disabled"
                              appendTo="self"
                              class="hh-form-dropdown" />
                </label>

                <label class="hh-row">
                    <span>Amount ($) <em>*</em></span>
                    <input v-model.number="form.amount" :disabled="disabled" type="number" min="0" step="0.01" />
                </label>

                <template v-if="isRecurring">
                    <label class="hh-row">
                        <span>Frequency <em>*</em></span>
                        <Dropdown v-model="form.frequency"
                                  :options="frequencies"
                                  optionLabel="text"
                                  optionValue="value"
                                  placeholder="Select..."
                                  :disabled="disabled"
                                  appendTo="self"
                                  class="hh-form-dropdown" />
                    </label>

                    <label class="hh-row">
                        <span>Start Date <em>*</em></span>
                        <input v-model="form.startDate" :disabled="disabled" type="date" />
                    </label>
                </template>

                <label v-else class="hh-row">
                    <span>Due Date</span>
                    <input v-model="form.dueDate" :disabled="disabled" type="date" />
                </label>

                <label v-if="!isRentCategory" class="hh-row">
                    <span>Vendor</span>
                    <Dropdown v-model="form.vendorId"
                              :options="vendors"
                              optionLabel="text"
                              optionValue="value"
                              placeholder="Select..."
                              :disabled="disabled"
                              appendTo="self"
                              class="hh-form-dropdown" />
                </label>

                <label class="hh-row">
                    <span>Collector <em>*</em></span>
                    <Dropdown v-model="form.collectorId"
                              :options="members"
                              optionLabel="text"
                              optionValue="value"
                              placeholder="Select..."
                              :disabled="disabled"
                              appendTo="self"
                              class="hh-form-dropdown" />
                </label>

                <label class="hh-field">
                    <span>Description</span>
                    <textarea v-model.trim="form.description"
                              :disabled="disabled"
                              rows="3"
                              placeholder="Enter description"></textarea>
                </label>

                <div class="hh-split">
                    <p>Split Between <em>*</em></p>

                    <details ref="memberDropdown"
                             :open="members.length <= 4">
                        <summary>{{ selectedMembers.length }} roommate(s) selected</summary>

                        <label class="hh-check">
                            <input type="checkbox"
                                   :checked="allMembersSelected"
                                   :disabled="disabled"
                                   @change="toggleAll($event.target.checked)" />
                            Select All
                        </label>

                        <label v-for="m in members" :key="m.value" class="hh-check">
                            <input v-model="selectedMemberIds"
                                   :disabled="disabled"
                                   type="checkbox"
                                   :value="String(m.value)" />
                            {{ m.text }}
                        </label>
                    </details>

                    <div class="hh-split__types">
                        <button v-for="type in splitTypes"
                                :key="type.value"
                                type="button"
                                :disabled="disabled"
                                :class="{ active: form.splitType === type.value }"
                                @click="setSplitType(type.value)">
                            {{ type.label }}
                        </button>
                    </div>

                    <p v-if="!selectedMembers.length" class="hh-error">
                        Select at least one roommate.
                    </p>

                    <div v-else class="hh-splits-table">
                        <div v-for="member in selectedMembers"
                             :key="member.value"
                             class="hh-split-row"
                             :class="{ 'hh-split-row--equal': form.splitType === 'Equal' }">

                            <span>{{ member.text }}</span>

                            <!-- Equal -->
                            <template v-if="form.splitType === 'Equal'">
                                <b>${{ equalShareAmount(member) }}</b>
                            </template>

                            <!-- Custom percentage -->
                            <template v-else-if="form.splitType === 'Percentage'">
                                <input :value="percentageDisplayValue(member)"
                                       :disabled="disabled"
                                       type="number"
                                       min="0"
                                       step="0.1"
                                       @focus="beginSplitInput(member)"
                                       @input="onPercentageInput(member, $event.target.value)"
                                       @blur="endSplitInput(member)" />
                                <b>%</b>
                                <span class="hh-split__calculated">
                                    ${{ splitAmount(member) }}
                                </span>
                            </template>

                            <!-- Fixed amount -->
                            <template v-else>
                                <b>$</b>
                                <input :value="fixedDisplayValue(member)"
                                       :disabled="disabled"
                                       type="number"
                                       min="0"
                                       step="0.01"
                                       @focus="beginSplitInput(member)"
                                       @input="onFixedInput(member, $event.target.value)"
                                       @blur="endSplitInput(member)" />

                                <!-- IMPORTANT: Fixed mode displays amount, not percentage -->
                                <span class="hh-split__calculated hh-fixed-calculated">
                                    ${{ Number(splitValues[String(member.value)] || 0).toFixed(2) }}
                                </span>
                            </template>
                        </div>

                        <div class="hh-split-total">
                            {{
 form.splitType === 'Percentage'
                                ? 'Total ' + splitTotal + '%'
                                : 'Total $' + splitTotal
                            }}
                        </div>
                    </div>

                    <p v-if="splitError" class="hh-error">{{ splitError }}</p>
                </div>

                <div class="hh-attach">
                    <button type="button" :disabled="disabled" @click="$refs.fileInput.click()">
                        Attach File
                    </button>
                    <input ref="fileInput"
                           type="file"
                           accept=".png,.gif,.jpeg,.jpg,.pdf"
                           hidden
                           @change="onFileChange" />
                    <span v-if="attachment">{{ attachment.name }}</span>
                </div>

                <p v-if="error" class="hh-error">{{ error }}</p>
            </div>

            <div class="hh-card__actions">
                <button type="button"
                        class="hh-primary"
                        :disabled="disabled || !!splitError"
                        @click="submit">
                    Confirm &amp; Submit
                </button>

                <button type="button"
                        class="hh-cancel"
                        :disabled="disabled"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </section>

        <!-- Created / failed result -->
        <section v-else-if="msg.type === 'household_expense_created' || msg.type === 'household_expense_failed'"
                 class="hh-card hh-result-card">
            <header :class="{ error: isError }">
                <div>
                    <h4>{{ isError ? 'Expense Not Saved' : 'Expense Created!' }}</h4>
                    <p>{{ isError ? 'Something went wrong.' : 'Your expense has been recorded.' }}</p>
                </div>
            </header>

            <div class="hh-result-card__body">
                <div><span>Title</span><b>{{ result.expenseTitle || '—' }}</b></div>
                <div><span>Type</span><b>{{ isResultRecurring ? 'Recurring' : 'One Time' }}</b></div>
                <div><span>Amount</span><b>{{ result.amount != null ? ('$' + Number(result.amount).toFixed(2)) : '—' }}</b></div>
                <div><span>Category</span><b>{{ result.categoryName || '—' }}</b></div>
                <div v-if="!isResultRecurring"><span>Vendor</span><b>{{ result.vendorName || '—' }}</b></div>
                <div>
                    <span>{{ isResultRecurring ? 'Frequency' : 'Due Date' }}</span>
                    <b>{{ isResultRecurring ? (result.frequency || '—') : formatMMDDYYYY(result.dueDate) }}</b>
                </div>
                <div v-if="isResultRecurring">
                    <span>Start Date</span>
                    <b>{{ formatMMDDYYYY(result.startDate) }}</b>
                </div>
                <div><span>Collector</span><b>{{ result.collectorName || '—' }}</b></div>
                <p v-if="isError" class="hh-error">{{ result.error || 'Please try again.' }}</p>
            </div>

            <div class="hh-result-card__footer">

                <span :class="{ 'hh-error-text': isError, 'hh-ok': !isError }">
                    {{ isError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </section>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';

    const blankForm = () => ({
        expenseTitle: '',
        amount: null,
        categoryId: '',
        vendorId: '',
        collectorId: '',
        description: '',
        frequency: '',
        startDate: '',
        dueDate: '',
        splitType: 'Equal',
        countryCode: '',
        phoneNumber: '',
    });

    const formatMMDDYYYY = (value) => {
        if (!value) return '—';

        const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

        return match
            ? `${match[2]}-${match[3]}-${match[1]}`
            : String(value);
    };

    export default {
        name: 'HouseholdExpenseFlow',

        components: {
            Dropdown,
        },

        props: {
            msg: {
                type: Object,
                required: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            currentDate: {
                type: String,
                default: '',
            },
        },

        emits: [
            'choose-type',
            'choose-option',
            'submit-expense',
            'cancel',
        ],

        data: () => ({
            form: blankForm(),
            selectedMemberIds: [],
            splitValues: {},
            attachment: null,
            error: '',
            countryCodeList: [],
            phoneLength: 10,
            phoneError: '',

            // Vendor popup selection is kept locally until Continue is clicked.
            selectedVendorValue: '',
            selectedVendorLabel: '',

            // Tracks manually changed Custom % rows.
            percentageEdited: {},

            // Preserve raw decimal text while the user is typing.
            activeSplitInputId: '',
            splitInputText: {},
        }),

        async created() {
            await this.GetCountryCodeList();
        },

        mounted() {
            document.addEventListener('click', this.handleOutsideMemberDropdownClick);
        },

        beforeUnmount() {
            document.removeEventListener('click', this.handleOutsideMemberDropdownClick);
        },

        computed: {
            data() {
                return this.msg?.payload?.household_expense_data || {};
            },

            prefilled() {
                return this.data.prefilled || {};
            },

            categories() {
                return this.data.categories || [];
            },

            vendors() {
                return this.data.vendors || [];
            },

            members() {
                return this.data.members || [];
            },

            frequencies() {
                return this.data.frequencies || [];
            },

            optionList() {
                return this.data.options || [];
            },

            responseText() {
                return this.msg?.payload?.response || this.msg?.text || '';
            },

            vendorPromptOptions() {
                if (Array.isArray(this.optionList) && this.optionList.length) {
                    return this.optionList;
                }

                const text = String(this.responseText || '');
                const options = [];
                const regex = /^\s*(\d+)\.\s+(.+?)\s*$/gm;

                let match;

                while ((match = regex.exec(text)) !== null) {
                    const label = String(match[2] || '').trim();

                    if (!label) continue;

                    options.push({
                        text: label,
                        value: String(match[1]),
                    });
                }

                return options;
            },

            isVendorPrompt() {
                const text = String(this.responseText || '')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return (
                    text.includes('vendor for this expense') ||
                    text.includes('any vendor') ||
                    text.includes('select vendor')
                );
            },

            isRecurring() {
                return this.prefilled.expenseType === 'recurring';
            },

            isResultRecurring() {
                return this.result.expenseType === 'recurring';
            },

            result() {
                return this.data || {};
            },

            resultCreatedDate() {
                return (
                    this.result.createdOn ||
                    this.result.CreatedOn ||
                    this.result.createdDate ||
                    this.result.CreatedDate ||
                    this.result.createdByDate ||
                    this.result.CreatedByDate ||
                    this.currentDate ||
                    ''
                );
            },

            isError() {
                return this.msg.type === 'household_expense_failed';
            },

            selectedMembers() {
                return this.members.filter(
                    m => this.selectedMemberIds.includes(String(m.value))
                );
            },

            allMembersSelected() {
                return (
                    this.members.length > 0 &&
                    this.selectedMemberIds.length === this.members.length
                );
            },

            isRentCategory() {
                const c = this.categories.find(
                    x => String(x.value) === String(this.form.categoryId)
                );

                return /rent/i.test(c?.text || '');
            },

            equalShare() {
                const amount = Number(this.form.amount) || 0;

                return this.selectedMembers.length
                    ? (amount / this.selectedMembers.length).toFixed(2)
                    : '0.00';
            },

            splitTypes() {
                return [
                    { value: 'Equal', label: 'Equal' },
                    { value: 'Percentage', label: 'Custom %' },
                    { value: 'Fixed', label: 'Fixed $' },
                ];
            },

            splitTotal() {
                if (this.form.splitType === 'Equal' || this.form.splitType === 'Fixed') {
                    return this.selectedMembers
                        .reduce(
                            (sum, m) =>
                                sum +
                                (Number(this.splitValues[String(m.value)]) || 0),
                            0
                        )
                        .toFixed(2);
                }

                return this.selectedMembers
                    .reduce(
                        (sum, m) =>
                            sum +
                            (Number(this.splitValues[String(m.value)]) || 0),
                        0
                    )
                    .toFixed(2);
            },

            splitError() {
                if (!this.selectedMembers.length) {
                    return 'Select at least one roommate.';
                }

                const expenseAmount = Number(this.form.amount) || 0;

                if (this.form.splitType === 'Percentage') {
                    const totalPercent = Number(this.splitTotal);

                    if (Math.abs(totalPercent - 100) > 0.0001) {
                        return '100% — must equal 100%.';
                    }
                }

                if (this.form.splitType === 'Fixed') {
                    const fixedTotal = Number(this.splitTotal) || 0;
                    const fixedPercent =
                        expenseAmount > 0
                            ? (fixedTotal / expenseAmount) * 100
                            : 0;

                    if (Math.abs(fixedPercent - 100) > 0.0001) {
                        return '100% — must equal 100%.';
                    }
                }

                if (this.form.splitType === 'Equal') {
                    const equalTotal = this.selectedMembers.reduce(
                        (sum, member) =>
                            sum + Number(this.equalShareAmount(member)),
                        0
                    );

                    if (Math.abs(equalTotal - expenseAmount) > 0.01) {
                        return `Total $${equalTotal.toFixed(2)} — must equal $${expenseAmount.toFixed(2)}.`;
                    }
                }

                return '';
            },
        },

        watch: {
            msg: {
                immediate: true,
                deep: true,
                handler() {
                    this.initialize();
                },
            },

            selectedMemberIds: {
                deep: true,
                handler() {
                    this.recalculateSplitValues();
                },
            },

            'form.amount': function () {
                this.recalculateSplitValues();
            },
        },

        methods: {
            handleOutsideMemberDropdownClick(event) {
                const dropdown = this.$refs.memberDropdown;

                if (!dropdown || !dropdown.open) {
                    return;
                }

                if (dropdown.contains(event.target)) {
                    return;
                }

                dropdown.open = false;
            },

            initialize() {
                // When a new vendor prompt is received, keep the popup visible
                // and clear any previous local selection.
                if (this.isVendorPrompt) {
                    this.selectedVendorValue = '';
                    this.selectedVendorLabel = '';
                    return;
                }

                if (this.msg?.type !== 'household_expense_form') {
                    return;
                }

                const p = this.prefilled;
                const currentCountryCode = this.form?.countryCode || '';

                this.form = {
                    ...blankForm(),
                    ...p,
                    expenseTitle: p.expenseTitle || '',
                    countryCode: p.countryCode || currentCountryCode,
                    phoneNumber: p.phoneNumber || '',
                };

                this.selectedMemberIds = this.members.map(
                    m => String(m.value)
                );

                this.splitValues = {};
                this.attachment = null;
                this.error = '';
                this.phoneError = '';

                this.recalculateSplitValues();
            },

            ensureSplitValues() {
                this.selectedMemberIds.forEach(id => {
                    if (this.splitValues[id] == null) {
                        this.splitValues[id] = 0;
                    }
                });
            },

            formatMMDDYYYY,

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response.data || [];

                    const us =
                        findDefaultUsCountryCode(this.countryCodeList) ||
                        this.countryCodeList.find(item =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        if (!this.form.countryCode) {
                            this.form.countryCode = us.value;
                        }

                        this.phoneLength =
                            resolvePhoneLength(
                                this.form.countryCode,
                                this.countryCodeList
                            ) || 10;
                    }
                } catch (e) {
                    console.error('Country code load failed', e);
                }
            },

            onCountryCodeChange() {
                this.phoneLength =
                    resolvePhoneLength(
                        this.form.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.form.phoneNumber = '';
                this.phoneError = '';
            },

            onPhoneInput(event) {
                const maxLength = this.phoneLength || 10;

                const digits = String(event.target.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                let formatted = digits;

                if (maxLength === 10) {
                    if (digits.length > 6) {
                        formatted =
                            `(${digits.slice(0, 3)}) ` +
                            `${digits.slice(3, 6)}-${digits.slice(6)}`;
                    } else if (digits.length > 3) {
                        formatted =
                            `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                    }
                }

                this.form.phoneNumber = formatted;
                this.phoneError = '';
            },

            setSplitType(type) {
                this.form.splitType = type;
                this.recalculateSplitValues();
            },

            getExactMemberCents() {
                const ids = [...this.selectedMemberIds];
                const count = ids.length;
                const result = {};

                if (!count) {
                    return result;
                }

                const totalCents = Math.round((Number(this.form.amount) || 0) * 100);
                const baseCents = Math.floor(totalCents / count);
                const remainderCents = totalCents - (baseCents * count);

                ids.forEach((id, index) => {
                    result[String(id)] =
                        baseCents + (index < remainderCents ? 1 : 0);
                });

                return result;
            },

            recalculateSplitValues() {
                const ids = [...this.selectedMemberIds];
                const count = ids.length;

                // Selection/amount/split-mode changes create a fresh exact split.
                this.percentageEdited = {};

                if (!count) {
                    this.splitValues = {};
                    return;
                }

                const exactCents = this.getExactMemberCents();
                const totalCents = Math.round((Number(this.form.amount) || 0) * 100);
                const newValues = {};

                ids.forEach(id => {
                    const memberCents = exactCents[String(id)] || 0;

                    if (this.form.splitType === 'Percentage') {
                        // Derive percentage from the exact cent allocation.
                        // Keep high precision internally so Custom $ display
                        // stays identical to Equal and Fixed.
                        newValues[String(id)] =
                            totalCents > 0
                                ? Number(((memberCents / totalCents) * 100).toFixed(8))
                                : 0;
                    } else {
                        // Equal / Fixed use the exact cent allocation.
                        newValues[String(id)] = memberCents / 100;
                    }
                });

                this.splitValues = newValues;
            },

            equalShareAmount(member) {
                const exactCents = this.getExactMemberCents();
                const cents = exactCents[String(member.value)] || 0;

                return (cents / 100).toFixed(2);
            },

            formatPercentage(value) {
                const number = Number(value);

                return Number.isFinite(number)
                    ? number.toFixed(1)
                    : '0.0';
            },

            beginSplitInput(member) {
                const id = String(member.value);
                this.activeSplitInputId = id;

                const currentValue = Number(this.splitValues[id]);

                let displayValue = '';

                if (Number.isFinite(currentValue)) {
                    displayValue =
                        this.form.splitType === 'Percentage'
                            ? currentValue.toFixed(1)
                            : currentValue.toFixed(2);
                }

                this.splitInputText = {
                    ...this.splitInputText,
                    [id]: displayValue,
                };
            },

            endSplitInput(member) {
                const id = String(member.value);

                if (this.activeSplitInputId === id) {
                    this.activeSplitInputId = '';
                }

                const copy = { ...this.splitInputText };
                delete copy[id];
                this.splitInputText = copy;
            },

            percentageDisplayValue(member) {
                const id = String(member.value);

                if (this.activeSplitInputId === id && this.splitInputText[id] != null) {
                    return this.splitInputText[id];
                }

                const value = Number(this.splitValues[id]);

                return Number.isFinite(value)
                    ? value.toFixed(1)
                    : '0.0';
            },

            onPercentageInput(member, value) {
                const id = String(member.value);

                this.splitInputText = {
                    ...this.splitInputText,
                    [id]: value,
                };

                if (value === '' || value === '.') {
                    return;
                }

                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    this.splitValues[id] = 0;
                    return;
                }

                const enteredValue = Math.max(0, numericValue);
                const otherMembers = this.selectedMembers.filter(
                    item => String(item.value) !== id
                );

                this.splitValues[id] = enteredValue;

                // Do not silently correct an invalid percentage.
                // Keep the user's value so splitError can block submission.
                if (enteredValue > 100) {
                    this.percentageEdited = { [id]: true };
                    return;
                }

                if (!otherMembers.length) {
                    this.splitValues[id] = enteredValue;
                    this.percentageEdited = { [id]: true };
                    return;
                }

                const remaining = Math.max(0, 100 - enteredValue);
                const equalBase = Math.floor((remaining * 10) / otherMembers.length) / 10;
                let distributed = 0;

                otherMembers.forEach((item, index) => {
                    const otherId = String(item.value);

                    if (index === otherMembers.length - 1) {
                        this.splitValues[otherId] =
                            Number((remaining - distributed).toFixed(1));
                    } else {
                        this.splitValues[otherId] = Number(equalBase.toFixed(1));
                        distributed += equalBase;
                    }
                });

                this.percentageEdited = {
                    [id]: true,
                };
            },

            fixedDisplayValue(member) {
                const id = String(member.value);

                if (this.activeSplitInputId === id && this.splitInputText[id] != null) {
                    return this.splitInputText[id];
                }

                const value = Number(this.splitValues[id]);

                return Number.isFinite(value)
                    ? value.toFixed(2)
                    : '0.00';
            },

            onFixedInput(member, value) {
                const id = String(member.value);

                this.splitInputText = {
                    ...this.splitInputText,
                    [id]: value,
                };

                if (value === '' || value === '.') {
                    return;
                }

                const numericValue = Number(value);
                const totalAmount = Number(this.form.amount) || 0;

                if (!Number.isFinite(numericValue)) {
                    this.splitValues[id] = 0;
                    return;
                }

                const enteredValue = Math.max(0, numericValue);

                const otherMembers = this.selectedMembers.filter(
                    item => String(item.value) !== id
                );

                this.splitValues[id] = enteredValue;

                // Do not silently clamp an invalid fixed amount.
                // Keep it so splitError prevents submission.
                if (enteredValue > totalAmount) {
                    return;
                }

                if (!otherMembers.length) {
                    this.splitValues[id] = enteredValue;
                    return;
                }

                const remainingCents = Math.max(
                    0,
                    Math.round((totalAmount - enteredValue) * 100)
                );

                const baseCents = Math.floor(
                    remainingCents / otherMembers.length
                );

                const remainderCents =
                    remainingCents -
                    (baseCents * otherMembers.length);

                otherMembers.forEach((item, index) => {
                    const otherId = String(item.value);
                    const cents =
                        baseCents +
                        (index < remainderCents ? 1 : 0);

                    this.splitValues[otherId] = cents / 100;
                });
            },

            splitAmount(member) {
                const id = String(member.value);
                const percent = Number(this.splitValues[id]) || 0;
                const totalCents =
                    Math.round((Number(this.form.amount) || 0) * 100);

                const memberCents =
                    Math.round((totalCents * percent) / 100);

                return (memberCents / 100).toFixed(2);
            },

            // Kept because it may be used elsewhere.
            splitPercentage(member) {
                const fixedAmount =
                    Number(
                        this.splitValues[String(member.value)]
                    ) || 0;

                const totalAmount =
                    Number(this.form.amount) || 0;

                return totalAmount
                    ? ((fixedAmount / totalAmount) * 100).toFixed(2)
                    : '0.00';
            },

            optionValue(option) {
                return typeof option === 'object'
                    ? (option.value ?? option.text)
                    : option;
            },

            optionLabel(option) {
                return typeof option === 'object'
                    ? (option.text ?? option.value)
                    : option;
            },

            chooseType(type) {
                this.$emit('choose-type', type);
            },

            chooseOption(option) {
                this.$emit(
                    'choose-option',
                    this.optionLabel(option),
                    this.optionValue(option)
                );
            },

            // Select vendor locally only. Do NOT emit here.
            // This keeps the popup visible and shows the selected radio state.
            selectVendor(option) {
                if (this.disabled || !option) {
                    return;
                }

                this.selectedVendorValue =
                    String(this.optionValue(option));

                this.selectedVendorLabel =
                    String(this.optionLabel(option));

                // First render the selected vendor in blue/check state,
                // then automatically continue to the next chatbot step.
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$emit(
                            'choose-option',
                            this.selectedVendorLabel,
                            this.selectedVendorValue
                        );
                    }, 250);
                });
            },

            isVendorSelected(option) {
                return (
                    String(this.selectedVendorValue) ===
                    String(this.optionValue(option))
                );
            },

            skipVendor() {
                if (this.disabled) {
                    return;
                }

                this.selectedVendorValue = '';
                this.selectedVendorLabel = '';

                this.$emit(
                    'choose-option',
                    'Skip',
                    'skip'
                );
            },

            toggleAll(checked) {
                this.selectedMemberIds = checked
                    ? this.members.map(m => String(m.value))
                    : [];
            },

            onFileChange(event) {
                this.attachment =
                    event.target.files?.[0] || null;
            },

            clearAttachment() {
                if (!this.attachment) {
                    this.$refs.fileInput.click();
                } else {
                    this.attachment = null;
                    this.$refs.fileInput.value = '';
                }
            },

            submit() {
                this.error = '';

                if (
                    !this.form.expenseTitle ||
                    !(Number(this.form.amount) > 0)
                ) {
                    this.error =
                        'Please fill in Title and Amount.';
                }

                if (
                    !this.error &&
                    this.form.phoneNumber
                ) {
                    const digits =
                        String(
                            this.form.phoneNumber || ''
                        ).replace(/\D/g, '');

                    const requiredLength =
                        this.phoneLength || 10;

                    if (!this.form.countryCode) {
                        this.phoneError =
                            'Please select a country code.';
                        this.error = this.phoneError;
                    } else if (
                        digits.length !== requiredLength
                    ) {
                        this.phoneError =
                            `Enter a valid ${requiredLength}-digit phone number.`;

                        this.error =
                            this.phoneError;
                    }
                }

                if (
                    !this.error &&
                    !this.form.collectorId
                ) {
                    this.error =
                        'Please select a Collector.';
                } else if (
                    !this.error &&
                    this.isRecurring &&
                    (
                        !this.form.frequency ||
                        !this.form.startDate
                    )
                ) {
                    this.error =
                        'Please select Frequency and Start Date.';
                } else if (
                    !this.error &&
                    this.splitError
                ) {
                    this.error =
                        this.splitError;
                }

                if (this.error) {
                    return;
                }

                const category =
                    this.categories.find(
                        x =>
                            String(x.value) ===
                            String(this.form.categoryId)
                    );

                const vendor =
                    this.vendors.find(
                        x =>
                            String(x.value) ===
                            String(this.form.vendorId)
                    );

                const collector =
                    this.members.find(
                        x =>
                            String(x.value) ===
                            String(this.form.collectorId)
                    );

                const totalAmount =
                    Number(this.form.amount) || 0;

                const prefilled = {
                    ...this.prefilled,
                };

                delete prefilled.splitBetween;
                delete prefilled.members;

                const exactCents = this.getExactMemberCents();

                const members =
                    this.selectedMembers.map(
                        (member) => {
                            const memberId = String(member.value);
                            let percentage = 0;
                            let amount = 0;

                            if (
                                this.form.splitType ===
                                'Equal'
                            ) {
                                const memberCents = exactCents[memberId] || 0;
                                amount = memberCents / 100;

                                percentage =
                                    totalAmount > 0
                                        ? +((amount / totalAmount) * 100).toFixed(8)
                                        : 0;
                            } else if (
                                this.form.splitType ===
                                'Percentage'
                            ) {
                                percentage =
                                    Number(this.splitValues[memberId]) || 0;

                                const totalCents =
                                    Math.round(totalAmount * 100);

                                const memberCents =
                                    Math.round((totalCents * percentage) / 100);

                                amount = memberCents / 100;
                            } else {
                                // Fixed $ keeps the value entered/shown for the member.
                                amount =
                                    Number(
                                        this.splitValues[
                                        memberId
                                        ]
                                    ) || 0;

                                percentage =
                                    totalAmount
                                        ? +(
                                            (
                                                amount /
                                                totalAmount
                                            ) * 100
                                        ).toFixed(8)
                                        : 0;
                            }

                            return {
                                userId: member.value,
                                percentage,
                                amount,
                            };
                        }
                    );

                this.$emit(
                    'submit-expense',
                    {
                        ...prefilled,
                        ...this.form,

                        expenseType:
                            String(
                                this.prefilled.expenseType ||
                                'one_time'
                            ).toLowerCase(),

                        amount:
                            totalAmount,

                        assetID:
                            this.prefilled.assetID,

                        assetName:
                            this.prefilled.assetName,

                        categoryName:
                            category?.text || '',

                        vendorName:
                            this.isRentCategory
                                ? ''
                                : (vendor?.text || ''),

                        vendorId:
                            this.isRentCategory
                                ? ''
                                : (this.form.vendorId || ''),

                        collectorName:
                            collector?.text || '',

                        splitType:
                            String(
                                this.form.splitType ||
                                'Equal'
                            ).toLowerCase(),

                        members,
                        attachment:
                            this.attachment,
                    }
                );
            },
        },
    };
</script>

<style scoped>
    .hh-card {
        font-family: inherit;
        color: #1e2d45;
        background: rgba(255, 255, 255, .97);
        border: 1px solid rgba(203, 213, 225, .75);
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 8px 24px rgba(15, 35, 65, .14);
        min-width: 300px;
        max-width: 390px;
        box-sizing: border-box;
    }

    .hh-flow-card__header,
    .hh-form-card__header,
    .hh-result-card > header {
        width: 100%;
        min-height: 58px;
        padding: 12px 16px;
        color: #fff;
        background: linear-gradient(135deg, #163f73 0%, #102a4d 100%);
        display: flex;
        align-items: center;
        gap: 10px;
        box-sizing: border-box;
        border-bottom: 1px solid rgba(255, 255, 255, .08);
    }

        .hh-form-card__header > div,
        .hh-result-card > header > div {
            flex: 1;
            min-width: 0;
        }

        .hh-form-card__header h4,
        .hh-result-card h4 {
            margin: 0;
            color: #fff !important;
            font-size: 15px;
            line-height: 1.25;
            font-weight: 700;
        }

        .hh-form-card__header p,
        .hh-result-card header p {
            margin: 3px 0 0;
            color: rgba(255, 255, 255, .88) !important;
            font-size: 11px;
            line-height: 1.35;
            font-weight: 500;
        }

    .hh-flow-card__options {
        padding: 9px;
    }

    .hh-option {
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 11px;
        border: 0;
        border-bottom: 1px solid #e5e7eb;
        background: #fff;
        color: #1e2d45;
        font-size: 13px;
        text-align: left;
        cursor: pointer;
    }

        .hh-option:last-child {
            border: 0;
        }

    .hh-form-card__body {
        padding: 12px 16px 14px;
        background: rgba(255, 255, 255, .96);
        box-sizing: border-box;
    }

    /* =========================
    Vendor popup
    ========================= */

    .hh-vendor-options {
        width: 100%;
        padding: 14px;
        box-sizing: border-box;
        background: #fff;
    }

    .hh-vendor-title {
        width: 100%;
        min-height: 38px;
        display: flex;
        align-items: center;
        margin: 0 0 10px;
        padding: 0 10px;
        border-bottom: 1px solid #edf0f3;
        color: #1e2d45;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
        box-sizing: border-box;
    }

    .hh-vendor-selectable {
        width: 100%;
        min-height: 42px;
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 0 8px;
        padding: 10px 12px;
        border: 1px solid #d9e0e8;
        border-radius: 8px;
        background: #f8fafc;
        color: #1e2d45;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
        transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
    }

        .hh-vendor-selectable:hover:not(:disabled) {
            background: #eef6ff;
            border-color: #0f66b8;
        }

        /* Selected vendor remains visible */
        .hh-vendor-selectable.hh-vendor-selected {
            background: #eef6ff;
            border-color: #0f66b8;
            box-shadow: 0 0 0 1px rgba(15, 102, 184, .08);
        }

    .hh-vendor-radio {
        width: 16px;
        height: 16px;
        min-width: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #b7c0cc;
        border-radius: 50%;
        background: #fff;
        box-sizing: border-box;
    }

    .hh-vendor-selectable:hover:not(:disabled) .hh-vendor-radio,
    .hh-vendor-selected .hh-vendor-radio {
        border-color: #0f66b8;
    }

    .hh-vendor-radio__dot {
        width: 8px;
        height: 8px;
        display: block;
        border-radius: 50%;
        background: #0f66b8;
    }

    .hh-vendor-name {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
        overflow-wrap: anywhere;
    }

    .hh-vendor-continue,
    .hh-vendor-skip {
        width: 100%;
        min-height: 38px;
        border-radius: 8px;
        padding: 9px 12px;
        font-family: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        box-sizing: border-box;
    }

    .hh-vendor-skip {
        margin: 0;
        border: 1px solid #d9e0e8;
        background: #fff;
        color: #667085;
    }

        .hh-vendor-skip:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #cbd5e1;
            color: #1e2d45;
        }

        .hh-vendor-selectable:disabled,
        .hh-vendor-skip:disabled {
            opacity: .55;
            cursor: not-allowed;
        }

    /* =========================
    Form fields
    ========================= */

    .hh-row {
        display: grid;
        grid-template-columns: minmax(92px, 1fr) 170px;
        align-items: center;
        column-gap: 14px;
        width: 100%;
        min-height: 46px;
        padding: 8px 0;
        border-bottom: 1px solid #edf0f3;
        box-sizing: border-box;
        font-size: 12px;
    }

        .hh-row > span,
        .hh-field > span,
        .hh-split > p {
            min-width: 0;
            color: #667085;
            font-weight: 700;
            line-height: 1.35;
        }

        .hh-row input {
            width: 170px;
            min-width: 170px;
            max-width: 170px;
            height: 34px;
            margin: 0;
            padding: 6px 9px;
            border: 1px solid #cbd5e1;
            border-radius: 7px;
            background: #fff;
            color: #344054;
            font: inherit;
            font-size: 12px;
            line-height: 1.2;
            box-sizing: border-box;
            outline: none;
        }

            .hh-row input:focus,
            .hh-field textarea:focus {
                border-color: #4f86bd;
                box-shadow: 0 0 0 2px rgba(79, 134, 189, .12);
            }

    .hh-form-dropdown {
        width: 170px;
        min-width: 170px;
        max-width: 170px;
    }

    :deep(.hh-form-dropdown.p-dropdown) {
        width: 170px;
        min-width: 170px;
        max-width: 170px;
        height: 34px;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        background: #fff;
        box-shadow: none;
        box-sizing: border-box;
    }

    :deep(.hh-form-dropdown.p-dropdown:not(.p-disabled):hover) {
        border-color: #94a3b8;
    }

    :deep(.hh-form-dropdown.p-dropdown:not(.p-disabled).p-focus) {
        border-color: #4f86bd;
        box-shadow: 0 0 0 2px rgba(79, 134, 189, .12);
    }

    :deep(.hh-form-dropdown .p-dropdown-label) {
        padding: 7px 8px;
        color: #344054;
        font-size: 12px;
        line-height: 18px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.hh-form-dropdown .p-dropdown-trigger) {
        width: 30px;
        min-width: 30px;
    }

    :deep(.hh-form-dropdown .p-dropdown-panel) {
        width: 100%;
        min-width: 100%;
        max-width: 100%;
        z-index: 99999 !important;
    }

    :deep(.hh-form-dropdown .p-dropdown-items-wrapper) {
        max-height: 190px !important;
        overflow-y: auto;
    }

    :deep(.hh-form-dropdown .p-dropdown-item) {
        padding: 8px 10px;
        font-size: 12px;
        line-height: 1.35;
        white-space: normal;
        word-break: normal;
        overflow-wrap: anywhere;
    }

    .hh-field {
        display: block;
        padding: 10px 0;
    }

        .hh-field span {
            display: block;
            margin-bottom: 5px;
        }

        .hh-field textarea {
            width: 100%;
            max-width: none;
            border: 1px solid #cbd5e1;
            border-radius: 7px;
            padding: 8px 9px;
            background: #fff;
            color: #344054;
            font: inherit;
            box-sizing: border-box;
            outline: none;
            resize: vertical;
        }

    .hh-form-card em,
    .hh-split em {
        color: #dc2626;
        font-style: normal;
    }

    /* =========================
    Split section
    ========================= */

    .hh-split {
        border-top: 1px solid #edf0f3;
        padding-top: 10px;
    }

        .hh-split > p {
            margin: 0 0 6px;
            font-size: 12px;
        }

        .hh-split details {
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            padding: 7px;
            font-size: 12px;
        }

        .hh-split summary {
            cursor: pointer;
        }

    .hh-check {
        display: block;
        padding: 5px 0;
    }

    .hh-split__types {
        display: flex;
        gap: 5px;
        margin-top: 9px;
    }

        .hh-split__types button {
            flex: 1;
            border: 1px solid #cbd5e1;
            background: #fff;
            border-radius: 7px;
            padding: 7px 3px;
            font-size: 11px;
            font-weight: 600;
            cursor: pointer;
        }

            .hh-split__types button.active,
            .hh-primary {
                background: #0f66b8;
                color: #fff;
                border-color: #0f66b8;
            }

    .hh-splits-table {
        margin-top: 9px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: hidden;
    }

    .hh-split-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 76px 12px 58px;
        align-items: center;
        column-gap: 3px;
        padding: 9px;
        border-bottom: 1px solid #edf0f3;
        font-size: 12px;
        box-sizing: border-box;
    }

        .hh-split-row > span:first-child {
            min-width: 0;
            padding-right: 5px;
            line-height: 1.4;
            word-break: normal;
            overflow-wrap: break-word;
        }

        .hh-split-row input {
            width: 76px;
            min-width: 0;
            height: 28px;
            border: 1px solid #cbd5e1;
            border-radius: 5px;
            padding: 4px 6px;
            box-sizing: border-box;
            font-size: 12px;
            font-weight: 400;
        }

        .hh-split-row > b {
            width: 12px;
            min-width: 12px;
            margin: 0;
            padding: 0;
            text-align: center;
            color: #1e2d45;
            font-size: 12px;
            line-height: 1.2;
            font-weight: 700;
            white-space: nowrap;
        }

        /* Fixed $ mode - show $ before the editable amount */
        .hh-split-row:has(.hh-fixed-calculated) {
            grid-template-columns: minmax(0, 1fr) 12px 76px 58px;
        }

            .hh-split-row:has(.hh-fixed-calculated) > b {
                grid-column: 2;
            }

            .hh-split-row:has(.hh-fixed-calculated) > input {
                grid-column: 3;
            }

    /* Fixed $ mode - keep same alignment as Custom % */
    .hh-fixed-calculated {
        grid-column: 4;
        width: 58px;
        min-width: 58px;
        justify-self: end;
        text-align: right;
        color: #1e2d45;
        font-size: 12px;
        line-height: 1.2;
        font-weight: 700;
        white-space: nowrap;
    }

    /* Equal split */
    .hh-split-row--equal {
        grid-template-columns: minmax(0, 1fr) auto;
        column-gap: 8px;
    }

        .hh-split-row--equal > b {
            justify-self: end;
            width: auto;
            min-width: 0;
            margin: 0;
            padding: 0;
            text-align: right;
            color: #1e2d45;
            font-size: 12px;
            line-height: 1.2;
            font-weight: 700;
            white-space: nowrap;
        }

    .hh-split__calculated {
        width: 58px;
        min-width: 58px;
        margin: 0;
        padding: 0;
        text-align: right;
        color: #1e2d45;
        font-size: 12px;
        line-height: 1.2;
        font-weight: 700;
        white-space: nowrap;
    }

    .hh-split-total {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 9px;
        border: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 700;
    }

    /* =========================
    Attachment/actions/results
    ========================= */

    .hh-attach {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-top: 10px;
        font-size: 11px;
    }

        .hh-attach button,
        .hh-secondary {
            border: 1px solid #cbd5e1;
            background: #f8fafc;
            color: #1e2d45;
            border-radius: 8px;
            padding: 8px;
            font-weight: 600;
            cursor: pointer;
        }

    .hh-card__actions {
        padding: 0 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .hh-primary {
        width: 100%;
        border: 0;
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        line-height: 16px;
    }

    .hh-cancel {
        width: 100%;
        border: 0;
        background: none;
        color: #aaa;
        padding: 4px;
        font-size: 12px;
        font-weight: 500;
        text-decoration: underline;
        cursor: pointer;
    }

    .hh-error {
        color: #dc2626;
        font-size: 11px;
        margin: 7px 0;
    }

    .hh-result-card > header.error {
        background: linear-gradient(135deg, #991b1b, #dc2626);
    }

    .hh-result-card__body {
        padding: 10px 16px;
    }

        .hh-result-card__body > div {
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid #edf0f3;
            padding: 7px 0;
            font-size: 12px;
        }

        .hh-result-card__body span {
            color: #667085;
        }

    .hh-result-card__footer {
        padding: 10px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 11px;
        background: #f8f6f2;
        border-top: 1px solid #e5e7eb;
    }

        .hh-result-card__footer span {
            font-size: 11px;
            white-space: nowrap;
        }

    .hh-error-text {
        color: #dc2626;
    }

    .hh-ok {
        color: #16a34a;
        font-weight: 700;
        font-size: 13px !important;
    }

    button:disabled,
    input:disabled,
    select:disabled,
    textarea:disabled {
        opacity: .55;
        cursor: not-allowed;
    }

    @media(max-width: 480px) {
        /* Mobile split design only */
        .hh-form-card__body {
            padding-left: 10px;
            padding-right: 10px;
        }

        .hh-splits-table {
            width: 100%;
            max-width: 100%;
            overflow: hidden;
        }

        .hh-split-row {
            width: 100%;
            grid-template-columns: minmax(0, 1fr) 60px 12px 52px;
            column-gap: 4px;
            padding: 9px 7px;
            align-items: center;
        }

            .hh-split-row > span:first-child {
                min-width: 0;
                padding-right: 4px;
                line-height: 1.25;
                font-size: 11px;
                word-break: normal;
                overflow-wrap: anywhere;
            }

            .hh-split-row input {
                width: 60px;
                max-width: 60px;
                min-width: 0;
                height: 30px;
                padding: 4px 6px;
                font-size: 11px;
            }

            .hh-split-row > b {
                width: 12px;
                min-width: 12px;
                font-size: 11px;
            }

        .hh-split__calculated,
        .hh-fixed-calculated {
            width: 52px;
            min-width: 52px;
            font-size: 10px;
            text-align: right;
            overflow: visible;
        }

        .hh-split-row:has(.hh-fixed-calculated) {
            grid-template-columns: minmax(0, 1fr) 12px 60px 52px;
        }

        .hh-split-row--equal {
            grid-template-columns: minmax(0, 1fr) 60px;
            column-gap: 6px;
        }

            .hh-split-row--equal > b {
                width: 60px;
                min-width: 60px;
                justify-self: end;
                text-align: right;
            }


        /* Expense Created card - mobile alignment only */
        .hh-result-card__body > div {
            display: grid;
            grid-template-columns: 72px minmax(0, 1fr);
            align-items: start;
            column-gap: 10px;
        }

            .hh-result-card__body > div > span {
                width: auto;
                min-width: 0;
            }

            .hh-result-card__body > div > b {
                min-width: 0;
                text-align: right;
                white-space: normal;
                overflow-wrap: anywhere;
                word-break: normal;
            }


        .hh-row {
            grid-template-columns: minmax(88px, 1fr) 156px;
            column-gap: 10px;
        }

            .hh-row input,
            .hh-form-dropdown,
            :deep(.hh-form-dropdown.p-dropdown) {
                width: 156px;
                min-width: 156px;
                max-width: 156px;
            }

        .hh-card {
            min-width: 0;
            width: 100%;
            max-width: none;
        }

        .hh-vendor-options {
            padding: 12px;
        }

        .hh-vendor-selectable {
            min-height: 40px;
            padding: 9px 11px;
        }
    }

    /* Vendor popup heading - blue header */
    .hh-vendor-title--blue {
        min-height: 46px;
        margin: -14px -14px 12px;
        padding: 0 14px;
        width: calc(100% + 28px);
        display: flex;
        align-items: center;
        box-sizing: border-box;
        border: 0;
        border-radius: 0;
        background: linear-gradient(135deg, #163f73 0%, #102a4d 100%);
        color: #ffffff;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
    }

    @media(max-width: 480px) {
        .hh-vendor-title--blue {
            margin: -12px -12px 12px;
            padding: 0 12px;
            width: calc(100% + 24px);
        }
    }
</style>
